import "../showroom/showroom-scrollbar";
import { deleteShowroom, showroomRequest, showroomToast } from "../showroom/showroom-api";

class ShowroomDrawer extends HTMLElement {
  constructor() {
    super();

    // Signed-in list: picking a showroom makes it the customer's active one
    // (custom.active_showroom, which the header link and product buttons
    // use) before opening it. "Create a showroom" hands over to the wizard.
    this.addEventListener("click", (event) => {
      const deleteButton = event.target.closest("[data-showroom-delete]");
      if (deleteButton) {
        event.preventDefault();
        this._deleteShowroom(deleteButton);
        return;
      }

      const swapLink = event.target.closest("[data-showroom-swap]");
      if (swapLink) {
        event.preventDefault();
        this._openShowroom(swapLink);
        return;
      }

      const panelButton = event.target.closest("[data-drawer-show]");
      if (panelButton) {
        this._showPanel(panelButton.dataset.drawerShow);
        return;
      }

      if (event.target.closest("[data-showroom-create]")) this._close();
    });
    this.closeButton = this.querySelector("[data-close]");
    this.content = this.querySelector(".showroom-drawer__content");

    this.closeButton?.addEventListener("click", () => this._close());
    this.addEventListener("click", (e) => {
      if (e.target.closest(".showroom-drawer__content")) return;

      this._close();
    });

    this.handleDocumentClick = (e) => {

      if (!document.body.contains(e.target)) return;

      if (!this.content.contains(e.target)) {
        this._close();
      }
    };

    document.addEventListener("click", this.handleDocumentClick);

    this.closeEsc = (e) => {
      if (e.key === "Escape") {
        this._close();
      }
    };

    document.addEventListener("keydown", this.closeEsc);

    document.addEventListener("showroom:open", () => this._open());
    document.addEventListener("showroom:close", () => this._close());

    document.addEventListener("drawer:before-open", (e) => {
      if (e.detail?.name !== "showroom") this._close();
    });
  }

  async _openShowroom(link) {
    const handle = link.dataset.showroomSwap;
    const activeHandle = window.showroomConfig?.customer?.showroom;

    if (handle && handle !== activeHandle) {
      link.classList.add("is-loading");
      const { ok } = await showroomRequest("swapActiveShowroom", { body: { showroom: handle }, showroom: handle });
      link.classList.remove("is-loading");

      // Still open it if the swap failed: the page works from its ?id= alone.
      if (!ok) showroomToast(window.showroomConfig?.strings?.genericError);
    }

    window.location.href = link.href;
  }

  // Owner or showroom admin (the showroom app checks). Removes the card, or
  // moves on to the next showroom when this one was active or open.
  async _deleteShowroom(button) {
    const item = button.closest(".showroom-drawer__item");
    item?.classList.add("is-loading");

    const stays = await deleteShowroom(button.dataset.showroomDelete);

    if (stays) {
      item?.remove();
    } else {
      item?.classList.remove("is-loading");
    }
  }

  // Login-mode panel: the main "Digital Showroom" screen and "How it works".
  _showPanel(name) {
    const panels = this.querySelectorAll("[data-drawer-panel]");
    if (!panels.length) return;

    panels.forEach((panel) => {
      panel.hidden = panel.dataset.drawerPanel !== name;
    });

    const scroll = this.querySelector("[data-drawer-scroll]");
    if (scroll) scroll.scrollTop = 0;

    this.querySelector(`[data-drawer-panel="${name}"] .showroom-drawer__title`)?.focus();
  }

  disconnectedCallback() {
    document.removeEventListener("keydown", this.closeEsc);
    document.removeEventListener("click", this.handleDocumentClick);
  }

  _toggle() {
    this.hasAttribute("open") ? this._close() : this._open();
  }

  _open() {
    if (this.hasAttribute("open")) return;

    document.dispatchEvent(new CustomEvent("markup-modal:close"));

    this.style.display = "block";
    this._showPanel("main");
    window.requestAnimationFrame(() => {
      this.setAttribute("open", "");
      window.openFullDimmer(this);
      window.lockScroll(this);
      this.returnFocusEl = document.activeElement;
      window.trapFocus(this, this.closeButton);
    });
  }

  _close() {
    if (!this.hasAttribute("open")) return;

    this.removeAttribute("open");
    window.closeFullDimmer(this);

    const onEnd = () => {
      this.removeEventListener("transitionend", onEnd);

      if (this.hasAttribute("open")) return;
      this.style.display = null;
      window.removeTrapFocus(this.returnFocusEl);
      window.unlockScroll(this);
    };
    this.addEventListener("transitionend", onEnd);
  }
}

if (!customElements.get("showroom-drawer")) {
  customElements.define("showroom-drawer", ShowroomDrawer);
}
